import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
  ViewEncapsulation,
  computed,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActiveConversationStore,
  ActivityService,
  ConversationHelperService,
  ConversationListComponent,
  ConversationStore,
  ConversationTypeStore,
  MessageStore,
  UserStore,
  WireApiService,
  GlobalErrorComponent,
  ErrorStore,
  DashboardHeaderComponent,
} from '../../../index';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Message, UserRole } from '@clientwire/api';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { WindowService } from '../../../services';
import { ConversationItemComponent } from '../../organisms';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'shared-main-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ConversationItemComponent,
    ConversationListComponent,
    ScrollingModule,
    CdkDropList,
    CdkDrag,
    TranslatePipe,
    GlobalErrorComponent,
    DashboardHeaderComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements AfterViewInit {
  generatedID = 'XU948-LT4';
  messageLimitToHaveScrollbar = 20;
  conversationListOpen = signal(false);
  #viewInitiated = signal(false);
  #contentInitStarted$ = new Subject<boolean>();
  readonly conversationsComponent = viewChild(ConversationListComponent);
  readonly conversationItemComponent = viewChild(ConversationItemComponent);

  readonly conversationStore = inject(ConversationStore);
  readonly activeConversationStore = inject(ActiveConversationStore);
  readonly conversationMessageStore = inject(MessageStore);
  readonly conversationTypeStore = inject(ConversationTypeStore);
  readonly userStore = inject(UserStore);
  readonly #windowService = inject(WindowService);
  readonly #activedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #activityService = inject(ActivityService);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly wireApiService = inject(WireApiService);
  readonly #errorStore = inject(ErrorStore);
  readonly #translateService = inject(TranslateService);
  readonly globalErrors = computed(() =>
    this.#errorStore.getErrorsByCodesAndType(
      ['error.unknown', 'error.identity.getUser', 'error.conversationType.get'],
      'global'
    )
  );
  readonly activeConversationList = viewChild('activeConversationList', { read: ElementRef });

  constructor() {
    const viewInitiated$ = toObservable(this.#viewInitiated);
    combineLatest([viewInitiated$, this.#activedRoute.queryParams])
      .pipe(takeUntil(this.#contentInitStarted$), takeUntilDestroyed())
      .subscribe(([initiated, queryParams]) => {
        if (initiated) {
          this.#initConversationsFromUrlAndStore(queryParams?.['conversationId']);
        }
      });
  }

  ngAfterViewInit(): void {
    this.conversationTypeStore.getConversationTypes();
    this.#windowService.calculateLimitSize(this.activeConversationList()?.nativeElement, true);
    this.#viewInitiated.set(true);
  }

  toggleConversationList() {
    this.conversationListOpen.update((state) => !state);
  }

  closeConversationList() {
    this.conversationListOpen.set(false);
  }

  changeActiveState(id: string) {
    const isActive = this.activeConversationStore.entityMap()[id];
    if (isActive) {
      this.activeConversationStore.removeConversation(id);
      this.#conversationHelperService.unsubscribeFromConversation(id);
    } else {
      const conversation = this.conversationStore.entityMap()[id];
      if (!conversation)
        throw new Error(
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.NO_ACTIVE_CONVERSATION')
        );
      conversation.conversationData = {
        ...conversation.conversationData,
        count: 0,
      };
      this.#conversationHelperService.addToActiveConversations([conversation]);
    }
  }

  setConversationInFocus(id: string) {
    this.activeConversationStore.setConversationIntoFocus(id);
  }

  handleConversationOpened(id: string) {
    this.conversationMessageStore.getMessagesOfConversation(
      id,
      this.#windowService.messagesLimit()
    );
  }

  handleMessageRead(message: Message) {
    if (message.conversationId === this.activeConversationStore.inFocusConversationId()) {
      this.#activityService.handleMessageSeenActivity(message);
    }
  }

  #initConversationsFromUrlAndStore(conversationId?: string) {
    this.#contentInitStarted$.next(true);
    if (conversationId) {
      this.activeConversationStore.addConversationToLocalStore(conversationId);
      // Clean the conversation from the url
      this.#router.navigate(['.'], {
        relativeTo: this.#activedRoute,
      });
    }

    this.#conversationHelperService.restoreActiveConversations();
  }

  drop(event: CdkDragDrop<string[]>) {
    const activeConversations = this.activeConversationStore.entities();
    moveItemInArray(activeConversations, event.previousIndex, event.currentIndex);
    this.activeConversationStore.swapIdsInLocalStorage(event.previousIndex, event.currentIndex);
  }

  reloadPage() {
    window.location.reload();
  }
}
